/**
 * SFX PROCEDURAL (10.4V) — sons gerados em tempo real via WebAudio (osciladores + ruído),
 * SEM arquivos de áudio (nada de asset/licença; combina com o projeto todo procedural).
 * O AudioManager chama play(id, vol) quando não há buffer registrado para o id.
 * `vol` = volume efetivo do canal (0..1). Tudo curto e discreto (mobile-friendly).
 */
export class SfxSynth {
  constructor(private readonly ctx: AudioContext) {}

  play(id: string, vol: number): void {
    if (vol <= 0) return;
    switch (id) {
      case "hit": // acerto em inimigo: thunk macio (triangle) + ruído curto filtrado
        this.blip(300, 130, "triangle", 0.18 * vol, 110);
        this.noise(0.05, 0.1 * vol, 1400);
        break;
      case "player_hit": // dano no player: thud grave macio
        this.blip(150, 170, "triangle", 0.24 * vol, 68);
        this.noise(0.09, 0.14 * vol, 520);
        break;
      case "dash": // arranco: whoosh subindo
        this.sweep(220, 620, 0.18, 0.18 * vol, "sawtooth", 900);
        break;
      case "level_up": // arpejo ascendente alegre
        this.arp([523, 659, 784, 1047], 0.09, 0.2 * vol, "triangle");
        break;
      case "chest_open": // baú: dois tons + brilho
        this.arp([494, 740], 0.11, 0.18 * vol, "triangle");
        this.blip(1320, 220, "sine", 0.1 * vol, 1600);
        break;
      case "crystal_break": // quebra de cristal: ruído agudo + ping
        this.noise(0.14, 0.22 * vol, 4200, "highpass");
        this.blip(1760, 180, "sine", 0.14 * vol, 2200);
        break;
      case "boss_defeated": // boss morto: boom grave descendo + ruído
        this.sweep(300, 60, 0.6, 0.3 * vol, "sawtooth", 500);
        this.noise(0.35, 0.22 * vol, 400);
        break;
      case "boss_theme": // "sting" do boss (curto; loop de música fica p/ fase futura)
        this.arp([98, 116, 147, 175], 0.14, 0.16 * vol, "sawtooth");
        break;
      case "rift_enter": // portal: whoosh grave + brilho
        this.sweep(140, 520, 0.45, 0.2 * vol, "sine", 1200);
        break;
      case "sanctuary_enter": // entrar na base: acorde quente curto
        this.chord([392, 494, 587], 0.32, 0.12 * vol, "sine");
        break;
      case "sanctuary_exit": // sair da base: descida suave
        this.sweep(520, 300, 0.22, 0.12 * vol, "sine");
        break;
      case "sanctuary_heal": // cura: ping alto suave
        this.blip(1046, 200, "sine", 0.1 * vol, 1320);
        break;
      case "sanctuary_block": // projétil bloqueado na base
        this.blip(660, 120, "square", 0.14 * vol, 440);
        break;
      // 10.9HK — MAGIAS com voz própria (antes tudo era level_up/hit)
      case "spell_bolt": // raio: estalo agudo descendo + chiado
        this.sweep(1800, 260, 0.16, 0.16 * vol, "sawtooth");
        this.noise(0.12, 0.12 * vol, 3200, "highpass");
        break;
      case "spell_meteor": // meteoro chamado: assobio caindo + rumor grave
        this.sweep(900, 140, 0.7, 0.1 * vol, "triangle");
        this.noise(0.6, 0.08 * vol, 420);
        break;
      case "spell_impact": // impacto pesado (meteoro/armadilha/golpe sísmico)
        this.noise(0.38, 0.24 * vol, 320);
        this.blip(110, 260, "sine", 0.2 * vol, 40);
        break;
      case "spell_whirl": // redemoinho: vento subindo
        this.sweep(260, 980, 0.55, 0.12 * vol, "triangle");
        this.noise(0.5, 0.1 * vol, 1400, "bandpass");
        break;
      case "spell_curse": // praga/nevasca: tom frio descendo
        this.sweep(520, 130, 0.6, 0.12 * vol, "sine");
        this.noise(0.45, 0.06 * vol, 900, "bandpass");
        break;
      case "spell_buff": // buff: acorde quente
        this.chord([523, 659, 784], 0.45, 0.09 * vol, "sine");
        this.blip(1046, 300, "triangle", 0.06 * vol);
        break;
      case "spell_trap": // armadilha armada: clique + tom curto
        this.noise(0.05, 0.14 * vol, 5000, "highpass");
        this.blip(880, 110, "square", 0.08 * vol, 420);
        break;
      case "spell_wave": // onda/linha: varredura grave → aguda
        this.sweep(120, 760, 0.28, 0.16 * vol, "sawtooth");
        this.noise(0.2, 0.1 * vol, 2400, "highpass");
        break;
      case "spell_summon": // invocação: arpejo sombrio
        this.arp([220, 277, 330, 440], 0.09, 0.09 * vol, "triangle");
        break;
      case "spell_volley": // chuva de flechas: silvo + estalos
        this.noise(0.25, 0.14 * vol, 3000, "highpass");
        this.blip(1200, 160, "triangle", 0.07 * vol, 600);
        break;
      default:
        this.blip(440, 120, "sine", 0.12 * vol);
    }
  }

  // ---- primitivas ----
  private gain(peak: number, dur: number, attack = 0.005): GainNode {
    const g = this.ctx.createGain();
    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur / 1000);
    g.connect(this.ctx.destination);
    return g;
  }

  /** Tom curto (ms), opcionalmente deslizando a frequência para `slideTo`. */
  private blip(freq: number, durMs: number, type: OscillatorType, peak: number, slideTo?: number): void {
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t + durMs / 1000);
    o.connect(this.gain(peak, durMs));
    o.start(t);
    o.stop(t + durMs / 1000 + 0.02);
  }

  /** Varredura de frequência (whoosh) em segundos. */
  private sweep(from: number, to: number, durS: number, peak: number, type: OscillatorType, _lp?: number): void {
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(from, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(1, to), t + durS);
    o.connect(this.gain(peak, durS * 1000, 0.01));
    o.start(t);
    o.stop(t + durS + 0.02);
  }

  /** Arpejo: notas em sequência. */
  private arp(freqs: number[], stepS: number, peak: number, type: OscillatorType): void {
    freqs.forEach((f, i) => window.setTimeout(() => this.blip(f, stepS * 1000 * 1.6, type, peak), i * stepS * 1000));
  }

  /** Acorde: notas simultâneas. */
  private chord(freqs: number[], durS: number, peak: number, type: OscillatorType): void {
    for (const f of freqs) this.blip(f, durS * 1000, type, peak);
  }

  /** Rajada de ruído filtrado (impacto/quebra). */
  private noise(durS: number, peak: number, filterFreq: number, filterType: BiquadFilterType = "lowpass"): void {
    const t = this.ctx.currentTime;
    const n = Math.floor(this.ctx.sampleRate * durS);
    const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < n; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / n); // decai
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = filterFreq;
    const g = this.ctx.createGain();
    g.gain.value = Math.max(0.0002, peak);
    src.connect(filter).connect(g).connect(this.ctx.destination);
    src.start(t);
    src.stop(t + durS + 0.02);
  }
}
